'use client';

import type { Column } from '@tanstack/react-table';
import { PlusCircle, XCircle } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { cn } from "@/lib/utils";
import { moneyStringOrNumberToMinorUnits, minorUnitsToMajorUnits } from "@/lib/money";

interface ConditionFilter {
  condition: string;
  value: number[]; // Always in minor units (cents)
}

interface DataTableMoneyFilterProps<TData> {
  column: Column<TData, unknown>;
  title?: string;
  currency?: string;
}

export function DataTableMoneyFilter<TData>({
  column,
  title,
  currency = 'USD',
}: DataTableMoneyFilterProps<TData>) {
  const { t } = useTranslation('common');
  const columnMeta = column.columnDef.meta;
  const options = columnMeta?.options || [];
  const formatter = columnMeta?.formatValue || ((minorUnits: number) => `$${(minorUnits / 100).toFixed(2)}`);

  const filterValue = column.getFilterValue() as ConditionFilter | undefined;

  // Single state object like NumberFilter - store in MINOR units like backend
  const [selectedValues, setSelectedValues] = React.useState<ConditionFilter>(() => {
    if (filterValue) return filterValue;
    return { condition: '', value: [] };
  });

  const [open, setOpen] = React.useState(false);

  // Sync local state with column filter value
  React.useEffect(() => {
    if (filterValue) {
      setSelectedValues(filterValue);
    } else {
      setSelectedValues({ condition: '', value: [] });
    }
  }, [filterValue]);

  const isFilterActive = React.useMemo(() => {
    return selectedValues.condition !== '' && selectedValues.value.length > 0;
  }, [selectedValues]);

  const columnFilterLabel = React.useMemo(() => {
    if (!isFilterActive) return undefined;

    const conditionOption = options.find((opt) => opt.value === selectedValues.condition);
    if (!conditionOption) return undefined;

    if (selectedValues.condition === 'is-between' && selectedValues.value.length === 2) {
      return `${formatter(selectedValues.value[0])} - ${formatter(selectedValues.value[1])}`;
    }

    return `${conditionOption.label} ${formatter(selectedValues.value[0])}`;
  }, [selectedValues, options, formatter, isFilterActive]);

  const isBetween = selectedValues.condition === 'is-between';

  // Convert minor units to major units for display in inputs
  const displayValues = React.useMemo(() => {
    return selectedValues.value.map((minorUnits) => minorUnitsToMajorUnits(minorUnits, currency));
  }, [selectedValues.value, currency]);

  const handleApply = () => {
    if (selectedValues.condition && selectedValues.value.length > 0) {
      column.setFilterValue(selectedValues);
    }
    setOpen(false);
  };

  const handleReset = () => {
    column.setFilterValue(undefined);
    setSelectedValues({ condition: '', value: [] });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn('border-dashed', isFilterActive && 'border-solid')}>
          {isFilterActive ? (
            <XCircle
              className="mr-2 size-4"
              onClick={(e) => {
                e.stopPropagation();
                handleReset();
              }}
            />
          ) : (
            <PlusCircle className="mr-2 size-4" />
          )}
          <span>{title}</span>
          {columnFilterLabel && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <span className="font-semibold">{columnFilterLabel}</span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{t('dataTable.filters.condition')}</Label>
            <Select
              value={selectedValues.condition}
              onValueChange={(value) => {
                setSelectedValues((prev) => {
                  const firstVal = prev.value[0] || 0;
                  const secondVal = prev.value[1] || 0;
                  return {
                    condition: value,
                    value: value === 'is-between' ? [firstVal, secondVal] : [firstVal],
                  };
                });
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder={t('dataTable.filters.selectCondition')} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedValues.condition && (
            <div className="space-y-1.5">
              <Label className="text-xs">{t('dataTable.filters.amount')}</Label>
              <div className="relative">
                <span className="text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 text-xs">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={displayValues[0] || ''}
                  onChange={(e) => {
                    const majorUnits = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    const minorUnits = Math.round(moneyStringOrNumberToMinorUnits(majorUnits, currency));
                    setSelectedValues((prev) => ({
                      ...prev,
                      value: isBetween ? [minorUnits, prev.value[1] || 0] : [minorUnits],
                    }));
                  }}
                  className="border-border/50 hover:border-border focus:border-border h-8 w-full rounded-md border bg-transparent pl-6 pr-2 text-xs outline-none transition-colors"
                />
              </div>

              {isBetween && (
                <>
                  <div className="text-muted-foreground text-center text-[10px]">
                    {t('dataTable.filters.to')}
                  </div>
                  <div className="relative">
                    <span className="text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 text-xs">
                      $
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={displayValues[1] || ''}
                      onChange={(e) => {
                        const majorUnits = e.target.value === '' ? 0 : parseFloat(e.target.value);
                        const minorUnits = Math.round(moneyStringOrNumberToMinorUnits(majorUnits, currency));
                        setSelectedValues((prev) => ({
                          ...prev,
                          value: [prev.value[0] || 0, minorUnits],
                        }));
                      }}
                      className="border-border/50 hover:border-border focus:border-border h-8 w-full rounded-md border bg-transparent pl-6 pr-2 text-xs outline-none transition-colors"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex gap-2 border-t pt-2">
            <Button
              size="sm"
              className="h-9 flex-1 text-xs sm:h-7"
              onClick={handleApply}
              disabled={!selectedValues.condition || selectedValues.value.length === 0}
            >
              {t('dataTable.filters.apply')}
            </Button>
            {isFilterActive && (
              <Button size="sm" variant="outline" className="h-9 flex-1 text-xs sm:h-7" onClick={handleReset}>
                {t('dataTable.filters.reset')}
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
